import React, {useState, useMemo} from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid
} from "@mui/material";
import {Input} from "@progress/kendo-react-inputs";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSelector } from "react-redux";
import { lockUser, unLockUser, resetUserPassword, updateUserEmail, isEmailAddressDuplicated } from "../../shared/api";
import WarningModal from "../generic-warning-modal";
import SuccesDialog from "../success-dialog";
import { isEmailValid } from "../../shared/shared";

const UserManagementModal = (props) => {


    const [userData, setUserData] = useState(props.user);
    const [emailEditable, setEmailEditable] = useState(false);
    const [emailAddress, setEmailAddress] = useState(props.user.email);
    const [backupEmail, setBackupEmail] = useState(props.user.email);
    const [showLockUserWarning, setShowLockUserWarning] = useState(false);
    const [showInvalidEmailDialog, setShowInvalidEmailDialog] = useState(false);
    const [showDuplicateEmailDialog, setShowDuplicateEmailDialog] = useState(false);
    const [showPasswordResetSuccessDialog, setShowPasswordResetSuccessDialog] = useState(false);

    const authToken = useSelector((state) =>
        state.dataStore.getIn(["loginInfo", "authorizationToken"])
    );

    const userId = useMemo(() => {
        return props.user.userId;
    }, [props.user]);

    const onLockUserClick = () => {
        setShowLockUserWarning(true);
    };

    const onUnlockUserClick = () => {
        setShowLockUserWarning(true);
    };
    
    const onResetUserPasswordClick = () => {
        resetUserPassword(userId, authToken).then(() => {
            setShowPasswordResetSuccessDialog(true);
        });
    };
    
    const handleClose = () => {
        props.close();
    };
    
    const onSaveEmailAddressClick = () => {
        if (!isEmailValid(emailAddress)) {
            setShowInvalidEmailDialog(true);
            return;
        }

        if (emailAddress === backupEmail) {
            return;
        }

        isEmailAddressDuplicated(emailAddress, authToken).then((isDuplicated) => {
            if (isDuplicated) {
                setShowDuplicateEmailDialog(true);
                return;
            }
            
            updateUserEmail(userId, emailAddress, authToken).then(() => {
                setBackupEmail(emailAddress);
                setEmailEditable(false);
            });
        })
       
    };
    
    const cancelEmailAddressChanges = () => {
        setEmailAddress(backupEmail);
        setEmailEditable(false);
    };

    return <Dialog
            fullWidth
            maxWidth={"sm"}
            open={true}
            onClose={() => {
                handleClose();
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            
            <DialogTitle>
                {props.user.firstName} {props.user.lastName}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={10}>
                        <Input value={emailAddress} 
                            disabled={!emailEditable} 
                            valid={() => {
                                return isEmailValid(emailAddress);
                            }} 
                            label={"Email Address"} 
                            onChange={(e) => {
                                setEmailAddress(e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        {!emailEditable && <EditIcon onClick={() => {
                            setEmailEditable(true);
                        }} />}
                        {emailEditable && <React.Fragment>
                            <CheckIcon onClick={onSaveEmailAddressClick} />
                            <CancelIcon onClick={cancelEmailAddressChanges} />
                        </React.Fragment>}
                    </Grid>
                    <Grid item xs={12}>
                        <Button onClick={onResetUserPasswordClick}>
                            Send Password Reset Email
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        {userData.isLocked && <Button onClick={onUnlockUserClick}>Unlock User</Button>}
                        {!userData.isLocked && <Button onClick={onLockUserClick}>Lock User</Button>}
                    </Grid>
                </Grid>
                {showLockUserWarning && <WarningModal 
                    open={true}
                    title={userData.isLocked ? "Unlock User" : "Lock User"}
                    message={userData.isLocked ? "Are you sure you want to unlock this user?" : "Are you sure you want to lock this user?"}
                    handleOk={() => {
                        const promise = userData.isLocked ? unLockUser(userId, authToken) : lockUser(userId, authToken);
                        promise.then(() => {
                            setUserData({
                                ...userData,
                                isLocked: !userData.isLocked
                            });
                            setShowLockUserWarning(false);
                        });
                    }}
                    handleClose={() => {
                        setShowLockUserWarning(false);
                    }}
                />}  
                {showPasswordResetSuccessDialog && <SuccesDialog 
                    open={true}
                    handleOk={() => {
                        setShowPasswordResetSuccessDialog(false);
                    }}
                    message={"Reset password email instructions have been successfully sent!"}
                />}                
                {showInvalidEmailDialog && <SuccesDialog 
                    open={true}
                    handleOk={() => {
                        setShowInvalidEmailDialog(false);
                    }}
                    message={"Please enter a valid email!"}
                />}                
                {showDuplicateEmailDialog && <SuccesDialog 
                    open={true}
                    handleOk={() => {
                        setShowDuplicateEmailDialog(false);
                    }}
                    message={"Email is already in use. Please enter a different email."}
                />}                  
            </DialogContent>
    </Dialog>
};

export default UserManagementModal;